import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";

/**
 * AnimatedCounter, counts from 0 to `to` when the element enters the viewport.
 *
 * Props:
 *  - to: final numeric value (supports decimals)
 *  - suffix: string appended after number (e.g. "+", "★")
 *  - duration: seconds (default 1.8)
 *  - decimals: number of decimal places to render (default auto-detected)
 */
export default function AnimatedCounter({ to = 0, suffix = "", duration = 1.8, decimals }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const mv = useMotionValue(0);
  const places = typeof decimals === "number"
    ? decimals
    : (String(to).includes(".") ? String(to).split(".")[1].length : 0);
  const rounded = useTransform(mv, (latest) => latest.toFixed(places));
  const [text, setText] = useState(places > 0 ? "0".padEnd(places + 2, "0").replace(/^0/, "0.") : "0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, {
      duration,
      ease: [0.22, 1, 0.36, 1],
    });
    const unsub = rounded.on("change", (v) => setText(v));
    return () => {
      controls.stop();
      unsub();
    };
  }, [inView, to, duration, mv, rounded]);

  return (
    <span ref={ref}>
      <motion.span>{text}</motion.span>
      {suffix && <span>{suffix}</span>}
    </span>
  );
}
